import React, { useState, useEffect } from 'react';
import supabase from '../utils/supabaseClient';

const CONTACT_PREF_KEY = 'totheroad_contact_pref';
const CONSENT_KEY = 'totheroad_cookie_consent';

const hasFunctionalConsent = (): boolean => {
  try {
    const consent = JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null');
    return consent?.functional === true;
  } catch {
    return false;
  }
};

const Contact: React.FC = () => {
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (hasFunctionalConsent()) {
      try {
        const pref = JSON.parse(localStorage.getItem(CONTACT_PREF_KEY) || 'null');
        if (pref) {
          setPrenom(pref.prenom || '');
          setNom(pref.nom || '');
          setEmail(pref.email || '');
        }
      } catch {
        // données corrompues, on ignore
      }
    }
  }, []);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSuccess(null);
    setError(null);

    const form = e.currentTarget;
    const formData = new FormData(form);
    const payload = {
      prenom: String(formData.get('prenom') || '').trim(),
      nom: String(formData.get('nom') || '').trim(),
      email: String(formData.get('email') || '').trim(),
      telephone: String(formData.get('phone') || '').trim() || null,
      sujet: String(formData.get('subject') || '').trim(),
      message: String(formData.get('message') || '').trim(),
    };

    try {
      const { error: insertError } = await supabase
        .from('contact')
        .insert([payload]);

      if (insertError) throw insertError;

      if (hasFunctionalConsent()) {
        localStorage.setItem(
          CONTACT_PREF_KEY,
          JSON.stringify({ prenom: payload.prenom, nom: payload.nom, email: payload.email })
        );
      }

      setSuccess("Votre message a bien été envoyé. Nous reviendrons vers vous rapidement.");
      form.reset();
      setPrenom('');
      setNom('');
      setEmail('');
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "Une erreur est survenue lors de l'envoi. Veuillez réessayer."
      );
    } finally {
      setSubmitting(false);
    }
  };
  return (
    /* On ajoute un fond de couleur pale bleuté en légé dégradé */
    <div className="min-h-screen py-10 bg-gradient-to-b from-blue-50 to-blue-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Contactez-nous
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Une question ? Notre équipe est là pour vous accompagner.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

          {/* Informations de contact */}
          <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Nos coordonnées</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-blue-500 mt-1 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-gray-900">Téléphone</h3>
                    <p className="text-gray-600">Bientôt disponible</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-blue-500 mt-1 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-gray-900">Email</h3>
                    <p className="text-gray-600">contact@totheroad.fr</p>
                  </div>
                </div>
                              <div className="flex items-start">
                  <svg className="w-6 h-6 text-blue-500 mt-1 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-gray-900">Lieu de départ</h3>
                    <p className="text-gray-600">Sud ouest Île-de-France, France</p>
                  </div>
                </div>
              </div>
              {/*Place holder léger gris arrondi pour la cadre google maps */}
              <div className="mt-8 rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 shadow-inner p-6">
              <div>{/* Cadre google maps avec pin sur la gare de versaille chantier et le zoom permet de voir l'ile de france*/}
                <iframe 
                  title='cadre_map' 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d168208.9636287216!2d2.031588452104261!3d48.79591966920892!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e67dcb71001b39%3A0xe3e37839d10a0c17!2sGare%20de%20Versailles-Chantiers!5e0!3m2!1sfr!2sfr!4v1765451923196!5m2!1sfr!2sfr"
                  width="100%"
                  height="530"
                  className="rounded-lg"
                  allowFullScreen={true} 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade">
                </iframe>
              </div>
              </div>

            </div>
          </div>

                    {/* Formulaire de contact */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Envoyez-nous un message</h2>
            {success && (
              <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-green-800">
                {success}
              </div>
            )}
            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
                {error}
              </div>
            )}
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="prenom" className="block text-sm font-medium text-gray-700 mb-2">
                  Prénom
                </label>
                <input
                  type="text"
                  required
                  id="prenom"
                  name="prenom"
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Votre prénom"
                />
              </div>
              <div>
                <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">
                  Nom
                </label>
                <input
                  type="text"
                  required
                  id="nom"
                  name="nom"
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Votre nom"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  required
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="votre@email.com"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Numéro de téléphone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Votre numéro de téléphone ( optionnel )"
                />
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Sujet
                </label>
                <select
                  required
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Sélectionnez un sujet</option>
                  <option value="reservation">Réservation</option>
                  <option value="information">Demande d'information</option>
                  <option value="support">Support technique</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  required
                  id="message"
                  name="message"
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Votre message..."
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className={`w-full text-white py-3 rounded-lg font-semibold transition-colors duration-200 ${
                  submitting
                    ? 'bg-blue-300 cursor-not-allowed'
                    : 'bg-blue-500 hover:bg-blue-600'
                }`}
              >
                {submitting ? 'Envoi en cours…' : 'Envoyer le message'}
              </button>
            </form>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default Contact;